import React from 'react';

/**
 * @component Logo
 * @description Componente reutilizable para mostrar logos de NFCores
 * Maneja diferentes variantes, tamaños y optimizaciones
 */

export interface LogoProps {
  /** Variante del logo */
  variant?: 'original' | 'white' | 'black' | 'sos-original' | 'sos-white' | 'sos-black';
  /** Tamaño del logo */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Clases CSS adicionales */
  className?: string;
  /** Texto alternativo */
  alt?: string;
  /** Si debe ser un enlace a la página principal */
  linkToHome?: boolean;
  /** Lazy loading */
  loading?: 'lazy' | 'eager';
}

const Logo: React.FC<LogoProps> = ({
  variant = 'original',
  size = 'md',
  className = '',
  alt,
  linkToHome = false,
  loading = 'lazy'
}) => {
  // Mapeo de variantes a archivos
  const logoFiles = {
    'original': '/assets/images/NFCores-original.png',
    'white': '/assets/images/NFCores-white.png',
    'black': '/assets/images/nfcores-original-black.png',
    'sos-original': '/assets/images/SOS NFC-original.png',
    'sos-white': '/assets/images/SOS NFC-white.png',
    'sos-black': '/assets/images/sosnfc-black.png'
  };

  // Mapeo de tamaños
  const sizeClasses = {
    'xs': 'h-6 w-auto',
    'sm': 'h-8 w-auto',
    'md': 'h-10 w-auto',
    'lg': 'h-12 w-auto',
    'xl': 'h-16 w-auto',
    '2xl': 'h-20 w-auto'
  };

  // Texto alternativo por defecto
  const defaultAlt = variant.includes('sos') ? 'NFCores SOS Logo' : 'NFCores Logo';

  const logoElement = (
    <img
      src={logoFiles[variant]}
      alt={alt || defaultAlt}
      className={`${sizeClasses[size]} ${className}`}
      loading={loading}
      // Optimizaciones adicionales
      decoding="async"
      // Preload para logos críticos - usar fetchpriority en minúsculas para HTML
      {...(loading === 'eager' && { fetchpriority: 'high' })}
    />
  );

  // Si debe ser un enlace, envolver en Link
  if (linkToHome) {
    return (
      <a
        href="/"
        className="inline-block focus-visible-ring rounded-lg"
        aria-label="Ir a la página principal"
      >
        {logoElement}
      </a>
    );
  }

  return logoElement;
};

export default Logo;

/**
 * Componentes especializados para casos comunes
 */

// Logo para navbar
export const NavbarLogo: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Logo
    variant="original"
    size="lg"
    linkToHome
    loading="eager"
    className={className}
    alt="NFCores - Página principal"
  />
);

// Logo para footer
export const FooterLogo: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Logo
    variant="white"
    size="md"
    className={className}
    alt="NFCores"
  />
);

// Logo SOS para páginas de producto
export const SOSLogo: React.FC<{ 
  variant?: 'original' | 'white' | 'black';
  size?: LogoProps['size'];
  className?: string;
}> = ({ 
  variant = 'original', 
  size = 'lg',
  className = '' 
}) => (
  <Logo
    variant={`sos-${variant}` as LogoProps['variant']}
    size={size}
    className={className}
    alt="SOS NFC - Tecnología de emergencia"
  />
);

// Logo compacto para espacios reducidos
export const CompactLogo: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Logo
    variant="original"
    size="sm"
    className={className}
    alt="NFCores"
  />
);
